import { useState, useEffect, useRef, useCallback } from "react";
import api from "../utils/api";
import { Search, X, MapPin, LoaderCircle, Check } from "lucide-react";

const initials = (name = "") =>
  name.trim().split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();

export function SiteSearchPopup({ value, onChange, label = "Site (optional)", placeholder = "No specific site" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [sites, setSites] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchSites = useCallback(async (search = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 30 });
      if (search) params.set("search", search);
      const { data } = await api.get(`/sites/admin?${params}`);
      setSites(data.data.sites || []);
      setTotal(data.data.total || 0);
    } catch { setSites([]); }
    finally { setLoading(false); }
  }, []);

  /* Resolve the selected site by id so the trigger shows its name on load.
     resolvedFor guards against re-fetching (a failed lookup would otherwise
     null `selected` and retrigger this effect forever). */
  const resolvedFor = useRef(null);
  useEffect(() => {
    if (!value) { setSelected(null); resolvedFor.current = null; return; }
    if (selected?._id === value || resolvedFor.current === value) return;
    resolvedFor.current = value;
    api.get(`/sites/admin/${value}`)
      .then(({ data }) => setSelected(data.data || null))
      .catch(() => setSelected(null));
  }, [value, selected]);

  /* Esc closes the dialog, and the page behind it shouldn't scroll */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey); };
  }, [open]);

  const openPopup = () => {
    setOpen(true);
    setQuery("");
    fetchSites("");
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  const handleSearch = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSites(q), 300);
  };

  const handleSelect = (site) => { setSelected(site); onChange(site._id); setOpen(false); };
  const handleClear = (e) => { e.stopPropagation(); setSelected(null); onChange(""); };

  return (
    <>
      {/* ── trigger ── */}
      <div className="flex flex-col gap-1.5">
        {label && <label className="text-xs font-semibold text-ink-600 tracking-wide">{label}</label>}
        <button
          type="button"
          onClick={openPopup}
          className="w-full flex items-center gap-2.5 pl-2.5 pr-3 py-2 rounded-xl border border-ink-300 bg-white text-sm text-left min-h-[46px]
                     hover:border-ink-400 focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all"
        >
          <span className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${selected ? "bg-brand-50 text-brand-600" : "bg-ink-100 text-ink-400"}`}>
            <MapPin size={15} />
          </span>
          {selected ? (
            <span className="flex-1 min-w-0">
              <span className="block font-medium text-ink-900 truncate">{selected.name}</span>
              {(selected.city || selected.code) && (
                <span className="block text-xs text-ink-400 truncate">{[selected.code, selected.city].filter(Boolean).join(" · ")}</span>
              )}
            </span>
          ) : (
            <span className="flex-1 text-ink-400 truncate">{placeholder}</span>
          )}
          <span className="flex items-center gap-1 shrink-0">
            {selected && (
              <span role="button" tabIndex={0} onClick={handleClear} onKeyDown={(e) => e.key === "Enter" && handleClear(e)}
                aria-label="Clear site"
                className="w-6 h-6 grid place-items-center rounded-full text-ink-400 hover:text-ink-700 hover:bg-ink-100 transition-colors">
                <X size={13} />
              </span>
            )}
            <Search size={15} className="text-ink-400" />
          </span>
        </button>
      </div>

      {/* ── dialog: bottom sheet on phones, centred card on desktop ── */}
      {open && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center sm:p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-[2px] animate-fade-in" onClick={() => setOpen(false)} />

          <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-pop border border-ink-200/60
                          overflow-hidden flex flex-col max-h-[88dvh] animate-sheet-up sm:animate-scale-in">
            <div className="sm:hidden pt-3 pb-1 grid place-items-center shrink-0">
              <span className="w-10 h-1.5 rounded-full bg-ink-200" />
            </div>

            <div className="px-4 pt-3 pb-3 border-b border-ink-100 shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-bold text-ink-900">Select Site</h3>
                <button type="button" onClick={() => setOpen(false)} aria-label="Close"
                  className="w-8 h-8 grid place-items-center rounded-lg text-ink-400 hover:text-ink-700 hover:bg-ink-100 transition-colors">
                  <X size={17} />
                </button>
              </div>
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                <input
                  ref={inputRef} type="text" value={query} onChange={handleSearch}
                  placeholder="Search by name, code, city, client…"
                  className="w-full pl-9 pr-3 py-2.5 text-sm bg-ink-50 border border-ink-200 rounded-xl text-ink-800 placeholder-ink-400
                             focus:outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:bg-white transition-all"
                />
              </div>
            </div>

            <div className="overflow-y-auto overscroll-contain flex-1 p-2">
              <button
                type="button"
                onClick={() => { onChange(""); setSelected(null); setOpen(false); }}
                className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors mb-1 ${
                  !value ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200" : "text-ink-500 hover:bg-ink-50"}`}
              >
                <span className="w-8 h-8 rounded-lg bg-ink-100 grid place-items-center text-ink-400 text-xs shrink-0">—</span>
                No specific site
              </button>

              {loading ? (
                <div className="flex justify-center py-10"><LoaderCircle size={20} className="animate-spin text-brand-500" /></div>
              ) : sites.length === 0 ? (
                <div className="text-center py-10 text-ink-400 text-sm">No sites found{query ? ` for “${query}”` : ""}</div>
              ) : (
                sites.map(site => {
                  const isSelected = value === site._id;
                  const client = [site.clientId?.name, site.clientId?.company].filter(Boolean).join(" — ");
                  const location = [site.city, site.state].filter(Boolean).join(", ");
                  return (
                    <button key={site._id} type="button" onClick={() => handleSelect(site)}
                      className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-colors mb-0.5 ${
                        isSelected ? "bg-brand-50 ring-1 ring-brand-200" : "hover:bg-ink-50"}`}>
                      <span className={`w-8 h-8 rounded-lg grid place-items-center text-[11px] font-bold shrink-0 ${
                        isSelected ? "bg-brand-200 text-brand-800" : "bg-ink-100 text-ink-500"}`}>
                        {initials(site.name)}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-1.5">
                          <span className={`text-sm font-medium truncate ${isSelected ? "text-brand-700" : "text-ink-800"}`}>{site.name}</span>
                          {site.code && <span className="text-ink-400 font-mono text-[11px] shrink-0">{site.code}</span>}
                        </span>
                        <span className="block text-xs text-ink-400 truncate">{[location, client].filter(Boolean).join(" · ") || "—"}</span>
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${
                          site.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-ink-100 text-ink-500"}`}>
                          {site.status || "—"}
                        </span>
                        {isSelected && <Check size={15} className="text-brand-600" />}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <div className="px-4 py-2.5 border-t border-ink-100 bg-ink-50/70 flex items-center justify-between shrink-0 safe-bottom">
              <span className="text-[11px] text-ink-400">{loading ? "Loading…" : `${sites.length} of ${total} sites`}</span>
              <span className="text-[11px] text-ink-400">Type to search all</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
